import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import type { PermissionKey } from "@laundrypro/shared";
import { Button, Card, EmptyState, FormSection } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { BusinessSettingsSection } from "./settings/BusinessSettingsSection";
import { BrandingSettingsSection } from "./settings/BrandingSettingsSection";
import { TaxSettingsSection } from "./settings/TaxSettingsSection";
import { PrintSettingsSection } from "./settings/PrintSettingsSection";
import { SecuritySettingsSection } from "./settings/SecuritySettingsSection";
import { DataSettingsSection } from "./settings/DataSettingsSection";
import { SystemSettingsSection } from "./settings/SystemSettingsSection";
import { AboutSection } from "./settings/AboutSection";

type SectionId =
  | "business" | "branding" | "tax" | "print"
  | "users" | "security" | "data" | "system" | "about";

interface SectionDef {
  id: SectionId;
  labelAr: string;
  labelEn: string;
  /** Hidden entirely when the caller lacks this. */
  permission?: PermissionKey;
}

const SECTIONS: readonly SectionDef[] = [
  { id: "business", labelAr: "بيانات المغسلة", labelEn: "Business details", permission: "settings.view" },
  { id: "branding", labelAr: "الهوية والعلامة التجارية", labelEn: "Identity and branding", permission: "branding.view" },
  { id: "tax", labelAr: "الضريبة", labelEn: "Tax", permission: "tax_settings.view" },
  { id: "print", labelAr: "الطباعة والفواتير", labelEn: "Printing and invoices", permission: "tax_settings.view" },
  { id: "users", labelAr: "المستخدمون والصلاحيات", labelEn: "Users and permissions", permission: "users.view" },
  { id: "security", labelAr: "الأمان والجلسات", labelEn: "Security and sessions" },
  { id: "data", labelAr: "النسخ الاحتياطي والبيانات", labelEn: "Backup and data", permission: "settings.view" },
  { id: "system", labelAr: "النظام", labelEn: "System", permission: "settings.view" },
  { id: "about", labelAr: "حول النظام", labelEn: "About" },
];

/**
 * One Settings area instead of nine separate admin pages.
 *
 * Owner configuration was previously scattered across sibling nav entries —
 * Settings, Branding, Tax, Users, Roles, Audit — which meant no single place
 * answered "how is this business set up?", and the nav carried six items that
 * were really one destination.
 *
 * The section list is PERMISSION-FILTERED, not just permission-disabled: a
 * section the caller cannot use does not appear, and cannot be selected by
 * default. The server re-checks every call regardless — this is courtesy, not
 * the control.
 */
export function SettingsHubPage() {
  const { hasPermission } = useAuth();
  const { language } = useBranding();
  const ar = language === "ar";

  const visible = SECTIONS.filter((s) => !s.permission || hasPermission(s.permission));
  const [section, setSection] = useState<SectionId>(() => visible[0]?.id ?? "about");
  const active = visible.find((s) => s.id === section) ?? visible[0];

  if (visible.length === 0) {
    return (
      <Card>
        <EmptyState
          title={ar ? "لا توجد إعدادات متاحة" : "No settings available"}
          description={ar ? "لا تملك صلاحية لأي قسم من الإعدادات." : "You don't have permission for any settings section."}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-ink-900">{ar ? "الإعدادات" : "Settings"}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {ar ? "كل ما يخص إعداد المغسلة في مكان واحد." : "Everything about how this business is set up."}
        </p>
      </div>

      {/*
        BarberPro's settings shell: a horizontally scrolling strip on a phone
        that becomes a fixed vertical rail on desktop, with the active section
        as a near-black pill. One element, two behaviours — no duplicated
        mobile/desktop navigation trees to drift apart.
      */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <nav
          aria-label={ar ? "أقسام الإعدادات" : "Settings sections"}
          // `min-w-0` is load-bearing, not tidying. As a flex item this nav
          // defaults to `min-width: auto`, which means it sizes to its own
          // content — nine section pills — and stretches the page instead of
          // scrolling inside it. Measured at 884px it pushed the document to
          // 997px and shifted every ancestor off-screen in RTL.
          className="flex min-w-0 gap-1.5 overflow-x-auto pb-1 lg:w-56 lg:shrink-0 lg:flex-col lg:overflow-visible lg:pb-0"
        >
          {visible.map((s) => (
            <button
              key={s.id}
              type="button"
              aria-current={section === s.id ? "page" : undefined}
              onClick={() => setSection(s.id)}
              className={`min-h-11 shrink-0 rounded-lg px-3 py-2 text-start text-sm transition-colors ${
                section === s.id
                  ? "bg-ink-900 font-medium text-white"
                  : "text-ink-600 hover:bg-ink-100"
              }`}
            >
              {ar ? s.labelAr : s.labelEn}
            </button>
          ))}
        </nav>

        <div className="min-w-0 flex-1 space-y-4">
          {active?.id === "business" && <BusinessSettingsSection />}
          {active?.id === "branding" && <BrandingSettingsSection />}
          {active?.id === "tax" && <TaxSettingsSection />}
          {active?.id === "print" && <PrintSettingsSection />}
          {active?.id === "users" && <UsersAndPermissionsSection ar={ar} />}
          {active?.id === "security" && <SecuritySettingsSection />}
          {active?.id === "data" && <DataSettingsSection />}
          {active?.id === "system" && <SystemSettingsSection />}
          {active?.id === "about" && <AboutSection />}
        </div>
      </div>
    </div>
  );
}

/**
 * Users and Roles keep their own full-page screens.
 *
 * They are not settings forms — they are working screens with tables, modals,
 * search and destructive actions, and cramming them into a settings panel
 * would make both worse. This section explains where they live and links to
 * them, which is honest about the structure rather than pretending everything
 * fits one shape.
 */
function UsersAndPermissionsSection({ ar }: { ar: boolean }) {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  return (
    <Card>
      <FormSection legend={ar ? "المستخدمون والصلاحيات" : "Users and permissions"}>
        <p className="mb-4 text-sm text-muted-foreground">
          {ar
            ? "إدارة الحسابات والأدوار لها شاشات كاملة — جداول وبحث وإجراءات حساسة — وتفتح خارج الإعدادات."
            : "Accounts and roles have full working screens — tables, search and sensitive actions — and open outside Settings."}
        </p>
        <div className="flex flex-wrap gap-2">
          {hasPermission("users.view") && (
            <Button onClick={() => navigate("/users")} variant="outline" size="sm" icon={<ExternalLink className="size-3.5" />}>
              {ar ? "المستخدمون" : "Users"}
            </Button>
          )}
          {hasPermission("roles.view") && (
            <Button onClick={() => navigate("/roles")} variant="outline" size="sm" icon={<ExternalLink className="size-3.5" />}>
              {ar ? "الأدوار والصلاحيات" : "Roles and permissions"}
            </Button>
          )}
          {hasPermission("audit_logs.view") && (
            <Button onClick={() => navigate("/audit-logs")} variant="outline" size="sm" icon={<ExternalLink className="size-3.5" />}>
              {ar ? "سجل التدقيق" : "Audit log"}
            </Button>
          )}
        </div>
      </FormSection>
    </Card>
  );
}
